import {ValueOf} from 'type-fest';

const PERFORMANCE_LATENCY_IDS = [
  'beforeWebflowDesignerLoads',
  'canvasBodyMounted',
  'canvasBodyRendered',
  'checkInvalidPages',
  'checkUrlParameters',
  'checkUtilityPageMissing',
  'constructSite',
  'dataManagerBridgeInit',
  'dispatchFest',
  'initialRender',
  'initializeSessionReplay',
  'loadSiteData',
  'migrateSite',
  'modernizeStyleData',
  'numVisitsSetup',
  'permissions',
  'plans',
  'postModernizeRemoteData',
  'preloadImages',
  'processSiteData',
  'queryCacheBridge',
  'removePreloader',
  'responsiveImageMigration',
  'responsiveImageSync',
  'retrieveSessionsNotification',
  'setDatadogContext',
  'syncStyles',
  'totalWebflowDesigner',
  'tutorialsSetup',
  'swatchesMirroredIntoVariables',
  'waitForAtomsIframe',
  'watchActiveDesignEvents',
  'watchKeyboardEvents',
  'wireUnloadBlocker',
] as const;

type PerformanceLatencyIdValue = ValueOf<typeof PERFORMANCE_LATENCY_IDS>;

type PerformanceMarkType = 'start' | 'end' | 'measure';

const generateMarkName = (
  key: PerformanceLatencyIdValue,
  type: PerformanceMarkType
) => {
  return `performance:${key}:${type}`;
};

/**
 * Starts a performance mark for any given key outlined above. Measurements
 * without a starting performance mark will use 0 (which is equivalent to the
 * start of the document loading).
 */
const startPerformanceMark = (key: PerformanceLatencyIdValue) => {
  performance.mark(generateMarkName(key, 'start'));
};

/**
 * Ends a performance mark for any given key outlined above. Measurements
 * without an ending performance mark will use the current time whenever
 * they are measured.
 */
const endPerformanceMark = (key: PerformanceLatencyIdValue) => {
  performance.mark(generateMarkName(key, 'end'));
};

const getPerformanceMark = (
  key: PerformanceLatencyIdValue,
  type: PerformanceMarkType
) => {
  const performanceMarks = performance.getEntriesByName(
    generateMarkName(key, type)
  );

  if (performanceMarks.length === 0) {
    return null;
  } else if (performanceMarks.length === 1) {
    return performanceMarks[0];
  }

  throw new Error(
    `We found several instances of performance mark ${type}:${key}`
  );
};

const measurePerformanceLatency = (key: PerformanceLatencyIdValue) => {
  try {
    const startMark = getPerformanceMark(key, 'start');
    const endMark = getPerformanceMark(key, 'end');
    const measureOptions: PerformanceMeasureOptions = {
      start: startMark?.startTime || undefined,
      end: endMark?.startTime || undefined,
    };

    const measure = performance.measure(
      generateMarkName(key, 'measure'),
      measureOptions
    );

    return Math.floor(measure.duration);
  } catch (err) {
    return null;
  }
};

const clearPerformanceMarks = (key: PerformanceLatencyIdValue) => {
  performance.clearMarks(generateMarkName(key, 'start'));
  performance.clearMarks(generateMarkName(key, 'end'));
  performance.clearMeasures(generateMarkName(key, 'measure'));
};

/**
 * Returns all performance latencies as an object with keys matching those
 * defined in PERFORMANCE_LATENCY_IDS and vaues being their duration in
 * milliseconds.
 *
 * These utils, although we could adapt them for other uses in the future,
 * are destined exclusively to the Designer load, and specifically to an
 * ongoing investigation for large latencies. Please do not use outside
 * this use case, or speak to @desux.
 */
const getAllLatencies = () => {
  return Object.fromEntries(
    PERFORMANCE_LATENCY_IDS.map((label) => {
      const latency = measurePerformanceLatency(label);
      clearPerformanceMarks(label);
      return [label, latency];
    })
  );
};

export {endPerformanceMark, getAllLatencies, startPerformanceMark};
